"use client";

import { useUser } from "@/lib/auth/client";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

const RETURN_TO_KEY = "returnTo";

// Accept only same-origin relative paths. Protocol-relative URLs
// ("//evil.com/...") and absolute URLs are rejected — every writer in the
// codebase stores `${pathname}${search}${hash}`, so anything else is either
// a hand-crafted URL we don't need to support or an open-redirect attempt.
export const isValidReturnTo = (returnTo: string): boolean =>
  !!returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//");

export const saveReturnTo = (returnTo: string | null | undefined): void => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  if (returnTo && isValidReturnTo(returnTo)) {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  }
};

export const peekReturnTo = (): string | null => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return null;
  }
  const stored = sessionStorage.getItem(RETURN_TO_KEY);
  if (stored && isValidReturnTo(stored)) {
    return stored;
  }
  if (stored) {
    sessionStorage.removeItem(RETURN_TO_KEY);
  }
  return null;
};

export const consumeReturnTo = (): string | null => {
  const value = peekReturnTo();
  if (value && typeof window !== "undefined" && window.sessionStorage) {
    sessionStorage.removeItem(RETURN_TO_KEY);
  }
  return value;
};

export const ReturnToHandler = () => {
  const { user, isLoading } = useUser();
  const pathname = usePathname();

  useEffect(() => {
    if (isLoading || !user || pathname === "/welcome") {
      return;
    }

    const returnTo = consumeReturnTo();
    if (returnTo) {
      window.location.href = returnTo;
    }
  }, [user, isLoading, pathname]);

  return null;
};
